import {Inject, Injectable} from '@angular/core';
import {DoseEntity, DoseType, Prescription, PrescriptionAction, PrescriptionDetailed, Schedule, TreatmentPillar} from "fitmed-models";
import {Observable, of} from "rxjs";
import {map, switchMap} from "rxjs/operators";
import {APP_CONFIG, AppConfig} from "@src/app.config";
import {RequestService} from "@services/http/request/request.service";
import {hasValue} from "utils";
import moment from "moment-timezone";
import {BuilderPrescription} from "@app/areas/prescriptions/models/builder-prescription";
import {PaginatedResult} from "@shared/models/data/shared/PaginatedResult";

export interface PrescriptionStats {
  authoredUsages: number;
  assignedUsages: number;
}

@Injectable({
  providedIn: 'root'
})
export class PrescriptionApiService {
  constructor(
    private requestService: RequestService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {
  }

  duplicatePrescription(prescriptionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/clone/${prescriptionId}`;
    return this.requestService.post(url, null).pipe(map(r => r.body))
  }

  duplicateAssignedPrescription(prescriptionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription/clone/${prescriptionId}`;
    return this.requestService.post(url, null).pipe(map(r => r.body))
  }

  createPrescription(name: string, prescriptions: BuilderPrescription[]): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions`;
    const payload = {
      name,
      prescriptions
    };

    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  upsertAuthoredDose(payload: any): Observable<DoseEntity> {
    const url = `${this.appConfig.angularEndpoint}/doses/upsertAuthored`;
    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  upsertAssignedDose(payload: any): Observable<DoseEntity> {
    const url = `${this.appConfig.angularEndpoint}/doses/upsertAssigned`;
    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  getDoses(prescriptionIds: string[]): Observable<DoseEntity[]> {
    const url = `${this.appConfig.angularEndpoint}/doses/listForPrescriptions`;
    const payload = {
      prescriptionIds
    };

    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  getAssignedDoses(prescriptionIds: string[]): Observable<DoseEntity[]> {
    const url = `${this.appConfig.angularEndpoint}/doses/listForAssignedPrescriptions`;
    const payload = {
      prescriptionIds
    };

    return this.requestService.post(url, payload).pipe(map(r => r.body))
  }

  update(payload: { prescriptionId: string, title?: string | null, customInstructions: string | null }): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions`;

    return this.requestService.put(url, payload).pipe(map(r => r.body))
  }

  updateAssigned(payload: { prescriptionId: string, customInstructions?: string | null }): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription`;
    return this.requestService.put(url, payload).pipe(map(r => r.body))
  }

  uploadPrescriptionImage(prescriptionId: string, file: File): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/image/${prescriptionId}`;
    return this.requestService.postFile(url, file).pipe(map(r => r.body))
  }

  updateTitle(prescriptionId: string, title: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/title/${prescriptionId}`;
    return this.requestService.put(url, {title}).pipe(map(r => r.body))
  }

  updateDescription(prescriptionId: string, desc: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/description/${prescriptionId}`;
    return this.requestService.put(url, {description: desc}).pipe(map(r => r.body))
  }

  updateAction(prescriptionId: string, action: PrescriptionAction): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/action/${prescriptionId}`;
    return this.requestService.put(url, {action}).pipe(map(r => r.body))
  }

  updateActionId(prescriptionId: string, actionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/actionId/${prescriptionId}`;
    return this.requestService.put(url, {actionId}).pipe(map(r => r.body))
  }

  updateTreatmentPillar(prescriptionId: string, treatmentPillar: TreatmentPillar): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/treatmentPillar/${prescriptionId}`;
    return this.requestService.put(url, {treatmentPillar}).pipe(map(r => r.body))
  }

  updateDoseTypes(prescriptionId: string, doseTypes: DoseType[]): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/doseTypes/${prescriptionId}`;
    return this.requestService.put(url, {doseTypes}).pipe(map(r => r.body))
  }

  assignPrescription(
    prescriptionId: string,
    schedule: Schedule,
    patientUserId: string,
    parentId: string | null,
    itemOrder: number | null,
  ): Observable<PrescriptionDetailed[] | null> {
    if (!hasValue(prescriptionId)) {
      return of(null)
    }

    // adding a child if the parent id has been provided upon assignment
    if (hasValue(parentId)) {
      return this.addAssignedChild(
        parentId,
        prescriptionId,
        patientUserId,
        schedule,
        itemOrder
      ).pipe(
        // switchMap(prescription => this.getAssignedPrescription(prescription.id)),
        map(p => [p])
      )
    }

    // Setting the start of the week
    let targetDate: moment.Moment | null = null;
    if (hasValue(schedule.startTime)) {
      targetDate = moment.utc(schedule.startTime);
    }

    if (hasValue(schedule.date)) {
      targetDate = moment.utc(schedule.date);
    }

    // ISO week starts on Monday (week starts sunday)
    let startTimeAsStartOfWeek: string | null = null;
    let endTimeAsStartOfWeek: string | null = null;

    if (hasValue(targetDate)) {
      startTimeAsStartOfWeek = targetDate
        .local()  // convert to local browser time
        .startOf('isoWeek')  // get the start of the week monday in local time
        .utc() // converting back to utc time
        .format();  // formatting for storage in the API

      endTimeAsStartOfWeek = targetDate.local().startOf('isoWeek').utc().format();
    }

    // sending null if there's no keys in the schedule
    schedule = Object.keys(schedule).length ? schedule : null;

    const body = {
      prescriptionId,
      schedule,
      patientUserId,
      startTimeAsStartOfWeek,
      endTimeAsStartOfWeek,
      parentId
    };

    const url = `${this.appConfig.angularEndpoint}/assignedPrescription/assignPrescription`;
    return this.requestService.post(url, body).pipe(map(r => r.body))
  }

  delete(prescriptionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions?prescriptionId=${prescriptionId}`;
    return this.requestService.delete(url).pipe(map(r => r.body))
  }

  deleteAdmin(prescriptionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/admin/${prescriptionId}`;
    return this.requestService.delete(url).pipe(map(r => r.body))
  }

  deleteAssigned(prescriptionId: string): Observable<Prescription> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription?prescriptionId=${prescriptionId}`;
    return this.requestService.delete(url).pipe(map(r => r.body))
  }

  deleteSeries(prescriptionId: string, scheduleGroupId: string): Observable<Prescription[]> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription/series?prescriptionId=${prescriptionId}&scheduleGroupId=${scheduleGroupId}`;
    return this.requestService.delete(url).pipe(map(r => r.body ?? []))
  }

  updateOrder(prescriptions: Prescription[]): Observable<Prescription[]> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/updateOrder`;
    return this.requestService.post(url, prescriptions)
      .pipe(
        map((r) => r.body),
      );
  }

  updateOrderAssigned(prescriptions: Prescription[]): Observable<Prescription[]> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription/updateOrder`;
    return this.requestService.post(url, prescriptions)
      .pipe(
        map((r) => r.body),
      );
  }

  /**
   * Links a child to a parent prescription
   * @param parentId the ID of the parent prescription
   * @param childId the ID of the child linking
   * @param assignedTo the assignedTo parameter
   * @param schedule the new schedule of the child
   * @param itemOrder the new itemOrder of the prescription item
   */
  addChild(
    parentId: string,
    childId: string,
    assignedTo: string | null,
    schedule: Schedule,
    itemOrder: number | null = null
  ): Observable<PrescriptionDetailed> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/childToPrescription`;

    // sending null if there's no keys in the schedule
    schedule = Object.keys(schedule).length ? schedule : null;

    const body = {
      parentPrescriptionId: parentId,
      childPrescriptionId: childId,
      assignedTo,
      schedule,
      itemOrder,
    }

    return this.requestService.post(url, body).pipe(map(r => r.body))
  }

  /**
   * Links a child to a parent prescription
   * @param parentId the ID of the parent prescription
   * @param childId the ID of the child linking
   * @param assignedTo the assignedTo parameter
   * @param schedule the new schedule of the child
   * @param itemOrder the new itemOrder of the prescription item
   */
  addAssignedChild(
    parentId: string,
    childId: string,
    assignedTo: string | null,
    schedule: Schedule,
    itemOrder: number | null = null
  ): Observable<PrescriptionDetailed> {
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription/childToPrescription`;

    // sending null if there's no keys in the schedule
    schedule = Object.keys(schedule).length ? schedule : null;

    const body = {
      parentPrescriptionId: parentId,
      childPrescriptionId: childId,
      assignedTo,
      schedule,
      itemOrder,
    }

    return this.requestService.post(url, body).pipe(map(r => r.body))
  }

  getPrescriptionVideo(id: string): Observable<Blob> {
    if (!hasValue(id)) {
      return of(null)
    }

    const url = `${this.appConfig.angularEndpoint}/prescriptions/video/${id}`;
    return this.requestService.getFile(url)
  }

  getPrescriptionVideoWithProgress(id: string): Observable<{ progress: number; blob?: Blob }> {
    if (!hasValue(id)) {
      return of({ progress: 0 })
    }

    const url = `${this.appConfig.angularEndpoint}/prescriptions/video/${id}`;
    return this.requestService.getFileWithProgress(url)
  }

  uploadPrescriptionVideo(id: string, file: File): Observable<any> {
    const url = `${this.appConfig.angularEndpoint}/prescriptions/video/${id}`;
    return this.requestService.postFile(url, file).pipe(map(r => r.body))
  }

  getPrescription(id: string): Observable<PrescriptionDetailed | null> {
    if (!hasValue(id)) {
      return of(null)
    }
    const url = `${this.appConfig.angularEndpoint}/prescriptions/details/${id}`;
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  getAssignedPrescription(id: string): Observable<PrescriptionDetailed | null> {
    if (!hasValue(id)) {
      return of(null)
    }
    const url = `${this.appConfig.angularEndpoint}/assignedPrescription?prescriptionId=${id}`;
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  getPrescriptionStats(id: string): Observable<PrescriptionStats> {
    if (!hasValue(id)) {
      return of({authoredUsages: 0, assignedUsages: 0});
    }

    const url = `${this.appConfig.angularEndpoint}/prescriptions/stats/${id}`;
    return this.requestService.get(url).pipe(map(r => r.body as PrescriptionStats));
  }

  loadForUser(userId: string, startUtc?: string | null, endUtc?: string | null): Observable<Prescription[]> {
    if (!hasValue(userId)) {
      return of([])
    }
    let url = `${this.appConfig.angularEndpoint}/prescriptions/assigned/${userId}`;
    if (hasValue(startUtc) && hasValue(endUtc)) {
      url += `?startUtc=${encodeURIComponent(startUtc)}&endUtc=${encodeURIComponent(endUtc)}`;
    }
    return this.requestService.get(url).pipe(map(r => r.body))
  }

  getPagedLibrary(params: {
    page: number;
    pageSize: number;
    search?: string | null;
    treatmentPillar?: TreatmentPillar | null;
    action?: PrescriptionAction | null;
    allItems?: boolean;
    mineOnly?: boolean;
    singleOnly?: boolean;
  }): Observable<PaginatedResult<Prescription>> {
    const query = new URLSearchParams();
    query.set('page', String(params.page));
    query.set('pageSize', String(params.pageSize));

    if (hasValue(params.search)) query.set('search', params.search);
    if (hasValue(params.treatmentPillar)) query.set('treatmentPillar', String(params.treatmentPillar));
    if (hasValue(params.action)) query.set('prescriptionAction', String(params.action));
    if (params.allItems) query.set('allItems', 'true');
    if (params.mineOnly) query.set('mineOnly', 'true');
    if (params.singleOnly) query.set('singleOnly', 'true');

    const url = `${this.appConfig.angularEndpoint}/prescriptions/paged?${query.toString()}`;
    return this.requestService.get(url).pipe(map(r => r.body));
  }
}
